import { useEffect, useState, JSX } from 'react';
import { TableCell, TableRow, Switch, Link, Box, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ICrawling, IGroup, IUser } from 'common';
import { Admin } from '../Admin';
import api from '../../services/api';
import { CrawlingFormFields } from '../../components/CrawlingFormFields';
import HashtagDeleteDialog from '../../components/HashtagDeleteDialog';
import { ADALONG_COLORS } from '@/styles';
import { style } from 'typestyle';

function Crawlings() {
  // _id -> group
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [users, setUsers] = useState<IUser[]>([]);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [crawlingToDelete, setCrawlingToDelete] = useState<ICrawling | null>(null);
  const displayHeader = (
    <TableRow>
      <TableCell key="1">Hashtag</TableCell>
      <TableCell key="2">Target Group</TableCell>
      <TableCell key="3">Daily Quota</TableCell>
      <TableCell key="4">Owner</TableCell>
      <TableCell key="5">Owner Group</TableCell>
      <TableCell key="6">Is Active?</TableCell>
      <TableCell key="7" />
    </TableRow>
  );

  const fillGroups = (): Promise<void> => {
    return api
      .getGroups({
        filters: {
          name: '',
        },
        options: {
          limit: 1000,
          skip: 0,
        },
      })
      .then((res) => {
        setGroups(res.groups);
        setIsLoadingGroups(false);
      });
  };

  const fillUsers = (): Promise<void> => {
    return api
      .getUsers({
        filters: {
          name: '',
        },
        options: {
          limit: 1000,
          skip: 0,
        },
      })
      .then((res) => setUsers(res.users));
  };

  useEffect(() => {
    Promise.all([fillGroups(), fillUsers()]).catch((e) => {
      console.error(`error while filling groups or users: ${e}`);
    });
  }, []);

  const save = (crawling: Partial<ICrawling>): Promise<void> => {
    const payload = {
      ...crawling,
      owner: crawling.owner?._id,
      group: crawling.group?._id,
    };
    return api.addOrUpdateCrawling(payload as Partial<ICrawling>).then();
  };

  const update: (crawling: Partial<ICrawling>) => Promise<void> = save;

  const search = (input: string): Promise<ICrawling[]> => {
    return api.getCrawlings(input);
  };

  const handleDeleteClick = (crawling: ICrawling) => {
    setCrawlingToDelete(crawling);
    setDeleteDialogOpen(true);
  };

  const handleDeleteBoth = async () => {
    if (!crawlingToDelete) return;
    
    // Delete the crawling
    await api.deleteCrawling(crawlingToDelete);
    
    // Also remove the corresponding tag from the group
    try {
      const groupId = crawlingToDelete.group?._id;
      if (groupId) {
        const groupsResponse = await api.getGroups({
          filters: { name: '' },
          options: { limit: 1000, skip: 0 }
        });
        const group = groupsResponse.groups.find(g => g._id === groupId);
        
        if (group && group.tags.includes(crawlingToDelete.hashtag)) {
          await api.updateGroup(groupId, {
            name: group.name,
            tags: group.tags.filter((t) => t !== crawlingToDelete.hashtag),
            featureFlags: group.feature_flags,
            termsUrl: group.terms_url,
            catalogCountries: group.catalogCountries || [],
          });
        }
      }
    } catch (e) {
      console.error('Error removing tag from group:', e);
    }
    
    setRefreshIndex(prev => prev + 1);
    setDeleteDialogOpen(false);
    setCrawlingToDelete(null);
  };

  const handleDeleteCrawlingOnly = async () => {
    if (!crawlingToDelete) return;
    
    // Only delete the crawling, keep the tag in the group
    await api.deleteCrawling(crawlingToDelete);
    
    setRefreshIndex(prev => prev + 1);
    setDeleteDialogOpen(false);
    setCrawlingToDelete(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCrawlingToDelete(null);
  };

  const toggleActive = (crawling: ICrawling): void => {
    const updatedCrawling = {
      ...crawling,
      active: !crawling.active,
    };
    api.addOrUpdateCrawling(updatedCrawling as Partial<ICrawling>)
      .then(() => {
        setRefreshIndex(prev => prev + 1);
      })
      .catch((e) => {
        console.error('Error toggling active status:', e);
      });
  };

  const displayLine = (crawling: ICrawling): JSX.Element[] => {
    const userGroup = groups.find(g => g._id === (crawling.owner.group as unknown as string));

    return [
      <TableCell key="1">{crawling.hashtag}</TableCell>,
      <TableCell key="2">{crawling.group?.name}</TableCell>,
      <TableCell key="3">{crawling.dailyQuota}</TableCell>,
      <TableCell key="4">{crawling.owner.email}</TableCell>,
      <TableCell key="5">{isLoadingGroups ? 'Loading…' : (userGroup?.name ?? '')}</TableCell>,
      <TableCell key="6">
            <Switch
              checked={crawling.active}
              onChange={() => toggleActive(crawling)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: ADALONG_COLORS.FLASH_BLUE,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#666666 !important',
                  opacity: 1,
                },
                '& .MuiSwitch-track': {
                  backgroundColor: '#666666', 
                  opacity: 1,
                },
              }}
            />
      </TableCell>,
      <TableCell key="7">
        <Link
          className={iconLink}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(crawling);
          }}
        >
          <DeleteIcon/>
        </Link>
      </TableCell>,
    ];
  };

  const displayEditWindow = (
    crawling: ICrawling | null,
    onValidItem: (line: Partial<ICrawling> | null) => void
  ): JSX.Element => {
    return <CrawlingFormFields crawling={crawling} groups={groups} users={users} onValidItem={onValidItem} />;
  };

  const customSection = (
    <Box sx={{ mb: 3, p: 2, bgcolor: ADALONG_COLORS.LIGHT_BLACK, borderRadius: '10px' }}>
      <Typography variant="body2" sx={{ color: ADALONG_COLORS.LIGHT_WHITE, mb: 1 }}>
        Each crawling is linked to a hashtag filter in Groups.
      </Typography>
      <Typography variant="body2" sx={{ color: ADALONG_COLORS.LIGHT_WHITE, mb: 0 }}>
        When deleting a crawling, you can choose to delete both the crawling and its associated filter, or only the crawling.
      </Typography>
    </Box>
  );

  return (
    <>
      <Admin<ICrawling>
        title="Crawlings"
        displayHeader={displayHeader}
        update={update}
        search={search}
        refreshIndex={refreshIndex}
        getId={(crawling) => crawling._id || 'unknown'}
        displayLine={displayLine}
        displayEditWindow={displayEditWindow}
        customSection={customSection}
      />
      {deleteDialogOpen && (
        <HashtagDeleteDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onDeleteBoth={handleDeleteBoth}
          onDeleteCrawlingOnly={handleDeleteCrawlingOnly}
        />
      )}
    </>
  );
}

const iconLink = style({ 
  padding: '0 6px', 
  cursor: 'pointer',
  color: ADALONG_COLORS.LIGHT_WHITE,
  $nest: {
    '& svg': {
      color: ADALONG_COLORS.LIGHT_WHITE,
    },
  },
});

export default Crawlings;